<template>
  <div class="categoria-lista-dashboard">
    <!-- Card de Cabeçalho -->
    <v-card class="header-card mb-5" flat>
      <v-card-title class="pa-4 d-flex align-center">
        <v-icon color="#023449" class="mr-2">mdi-shape</v-icon>
        <span class="header-title">Categorias de Normativas</span>
        <v-spacer></v-spacer>

        <!-- Toggle de Visualização -->
        <v-btn-toggle
            v-model="modoVisualizacao"
            mandatory
            dense
        >
          <v-btn small value="lista">
            <v-icon small>mdi-view-list</v-icon>
          </v-btn>
          <v-btn small value="cards">
            <v-icon small>mdi-view-grid</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-divider class="mx-5 mr-5" vertical/>

        <v-chip small color="#e8f5e9" text-color="#2e7d32">
          <v-icon left x-small color="#2e7d32">mdi-shape</v-icon>
          {{ categorias.length }} {{ categorias.length === 1 ? 'categoria' : 'categorias' }}
        </v-chip>

        <v-btn
            color="#023449"
            dark
            class="action-btn ml-3"
            small
            @click="abrirFormulario()"
            :loading="carregando"
            elevation="0"
        >
          <v-icon left small>mdi-plus</v-icon>
          Nova Categoria
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Loading -->
    <div v-if="carregando && categorias.length === 0" class="loading-state">
      <v-progress-circular
          indeterminate
          color="#023449"
          size="64"
      ></v-progress-circular>
      <p class="mt-4 loading-text">Carregando categorias...</p>
    </div>

    <!-- Lista Vazia -->
    <div v-else-if="!carregando && categorias.length === 0" class="no-data-message">
      <v-icon size="64" color="#cbd5e1">mdi-folder-open-outline</v-icon>
      <h3>Nenhuma categoria cadastrada</h3>
      <p>Crie categorias para organizar suas normativas</p>
      <v-btn
          color="#023449"
          dark
          outlined
          @click="abrirFormulario()"
          class="mt-4"
      >
        <v-icon left>mdi-plus</v-icon>
        Criar Primeira Categoria
      </v-btn>
    </div>

    <!-- Visualização em Cards -->
    <v-container v-else-if="modoVisualizacao === 'cards'" fluid class="pa-0">
      <v-row>
        <v-col
            v-for="categoria in categorias"
            :key="categoria.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
        >
          <v-card
              class="categoria-card"
              :class="{ 'categoria-inativa': categoria.ativo === 'F' }"
              hover
          >
            <!-- Menu de Ações (Canto Superior Direito) -->
            <v-menu offset-y left :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    small
                    class="card-menu-btn"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="processando"
                >
                  <v-icon size="22" color="white">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="visualizarDetalhes(categoria)">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="#023449">mdi-eye</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="menu-item-text">Visualizar</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item @click="abrirFormulario(categoria)">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="#023449">mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="menu-item-text">Editar</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-1"></v-divider>

                <v-list-item
                    @click="confirmarInativacao(categoria)"
                    :disabled="categoria.ativo === 'F'"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="menu-item-text error--text">
                      Inativar
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Header Colorido -->
            <div
                class="categoria-header"
                :style="{ backgroundColor: categoria.cor }"
            >
              <v-icon large color="white">{{ categoria.icone }}</v-icon>
            </div>

            <!-- Conteúdo -->
            <v-card-text class="pa-4">
              <h3 class="categoria-nome mb-3">{{ categoria.nome }}</h3>

              <div class="categoria-info">
                <div class="info-item">
                  <v-icon small color="#64748b" class="mr-2">mdi-palette</v-icon>
                  <span class="info-text">{{ categoria.cor }}</span>
                </div>

                <div class="info-item">
                  <v-icon small color="#64748b" class="mr-2">mdi-calendar-clock</v-icon>
                  <span class="info-text">{{ formatarData(categoria.data_criacao) }}</span>
                </div>

                <div v-if="categoria.ativo === 'F'" class="info-item">
                  <v-chip x-small color="grey" dark>
                    <v-icon left x-small>mdi-cancel</v-icon>
                    INATIVA
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Visualização em Lista -->
    <v-card v-else outlined class="lista-view">
      <v-data-table
          :headers="headersLista"
          :items="categorias"
          :items-per-page="10"
          :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, -1],
          'items-per-page-text': 'Itens por página:'
        }"
          class="categoria-table"
          no-data-text="Nenhuma categoria encontrada"
      >
        <!-- Coluna Categoria -->
        <template v-slot:item.categoria="{ item }">
          <div class="d-flex align-center py-0">
            <div
                class="lista-icon-wrapper mr-3"
                :style="{ backgroundColor: item.cor }"
            >
              <v-icon color="white" size="20">{{ item.icone }}</v-icon>
            </div>
            <div>
              <div class="lista-nome">{{ item.nome }}</div>
              <div class="lista-id">ID: {{ item.id }}</div>
            </div>
          </div>
        </template>

        <!-- Coluna Cor -->
        <template v-slot:item.cor="{ item }">
          <v-chip
              small
              :color="item.cor"
              dark
              class="font-weight-medium"
          >
            {{ item.cor }}
          </v-chip>
        </template>

        <!-- Coluna Status -->
        <template v-slot:item.ativo="{ item }">
          <v-chip
              small
              :color="item.ativo === 'T' ? 'success' : 'grey'"
              dark
          >
            <v-icon left x-small>
              {{ item.ativo === 'T' ? 'mdi-check-circle' : 'mdi-cancel' }}
            </v-icon>
            {{ item.ativo === 'T' ? 'Ativa' : 'Inativa' }}
          </v-chip>
        </template>

        <!-- Coluna Data -->
        <template v-slot:item.data_criacao="{ item }">
          <div class="lista-date">
            <v-icon x-small color="#64748b" class="mr-1">mdi-calendar</v-icon>
            {{ formatarData(item.data_criacao) }}
          </div>
        </template>

        <!-- Coluna Ações -->
        <template v-slot:item.acoes="{ item }">
          <div class="d-flex align-center justify-end">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    @click="visualizarDetalhes(item)"
                    v-bind="attrs"
                    v-on="on"
                    color="#023449"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Visualizar</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    @click="abrirFormulario(item)"
                    v-bind="attrs"
                    v-on="on"
                    color="#023449"
                    class="ml-1"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    @click="confirmarInativacao(item)"
                    v-bind="attrs"
                    v-on="on"
                    color="error"
                    class="ml-1"
                    :disabled="item.ativo === 'F'"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Inativar</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Formulário de Categoria -->
    <CategoriaFormulario
        :dialog.sync="dialogFormulario"
        :categoria="categoriaEditando"
        @salvo="handleCategoriaSalva"
        @fechar="handleFormularioFechado"
    />

    <!-- Dialog de Confirmação de Inativação -->
    <v-dialog
        v-model="dialogConfirmacao"
        max-width="500px"
        persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="warning">mdi-alert</v-icon>
          Confirmar Inativação
        </v-card-title>

        <v-card-text class="pb-0">
          <p>
            Tem certeza que deseja inativar a categoria
            <strong>{{ categoriaSelecionada?.nome }}</strong>?
          </p>
          <v-alert
              type="warning"
              dense
              text
              class="mt-2 mb-0"
          >
            Categorias com normativas ativas não podem ser inativadas.
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn
              text
              @click="dialogConfirmacao = false"
              :disabled="processando"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              color="error"
              @click="inativarCategoria"
              :loading="processando"
          >
            <v-icon left>mdi-delete</v-icon>
            Inativar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Detalhes -->
    <v-dialog
        v-model="dialogDetalhes"
        max-width="600px"
    >
      <v-card v-if="categoriaSelecionada">
        <v-card-title class="pa-4 white--text" :style="{ backgroundColor: categoriaSelecionada.cor }">
          <v-icon left large color="white">{{ categoriaSelecionada.icone }}</v-icon>
          <span class="dialog-title">{{ categoriaSelecionada.nome }}</span>
          <v-spacer></v-spacer>
          <v-btn
              icon
              dark
              @click="dialogDetalhes = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-simple-table>
            <tbody>
            <tr>
              <td class="font-weight-bold detail-label" width="150">ID:</td>
              <td class="detail-value">{{ categoriaSelecionada.id }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold detail-label">Nome:</td>
              <td class="detail-value">{{ categoriaSelecionada.nome }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold detail-label">Cor:</td>
              <td class="detail-value">
                <v-chip
                    small
                    :color="categoriaSelecionada.cor"
                    dark
                >
                  {{ categoriaSelecionada.cor }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold detail-label">Ícone:</td>
              <td class="detail-value">
                <v-icon :color="categoriaSelecionada.cor" class="mr-2">
                  {{ categoriaSelecionada.icone }}
                </v-icon>
                {{ categoriaSelecionada.icone }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold detail-label">Status:</td>
              <td class="detail-value">
                <v-chip
                    small
                    :color="categoriaSelecionada.ativo === 'T' ? 'success' : 'grey'"
                    dark
                >
                  {{ categoriaSelecionada.ativo === 'T' ? 'Ativa' : 'Inativa' }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold detail-label">Criada em:</td>
              <td class="detail-value">{{ formatarDataCompleta(categoriaSelecionada.data_criacao) }}</td>
            </tr>
            <tr v-if="categoriaSelecionada.data_alteracao">
              <td class="font-weight-bold detail-label">Alterada em:</td>
              <td class="detail-value">{{ formatarDataCompleta(categoriaSelecionada.data_alteracao) }}</td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
              color="#023449"
              dark
              text
              @click="dialogDetalhes = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
        bottom
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import CategoriaFormulario from './CategoriaFormulario.vue'
import { mapState } from 'vuex'

export default {
  name: 'CategoriaLista',

  components: {
    CategoriaFormulario
  },

  data() {
    return {
      categorias: [],
      carregando: false,
      processando: false,
      modoVisualizacao: 'lista', // 'cards' ou 'lista'
      dialogFormulario: false,
      dialogConfirmacao: false,
      dialogDetalhes: false,
      categoriaEditando: null,
      categoriaSelecionada: null,

      headersLista: [
        { text: 'Categoria', value: 'categoria', sortable: true, width: '35%' },
        { text: 'Cor', value: 'cor', sortable: true, align: 'center', width: '15%' },
        { text: 'Status', value: 'ativo', sortable: true, align: 'center', width: '15%' },
        { text: 'Data de Criação', value: 'data_criacao', sortable: true, align: 'center', width: '20%' },
        { text: 'Ações', value: 'acoes', sortable: false, align: 'end', width: '15%' }
      ],

      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },

  mounted() {
    this.carregarCategorias()
    // Carregar preferência de visualização do localStorage
    const modoSalvo = localStorage.getItem('categorias_modo_visualizacao')
    if (modoSalvo) {
      this.modoVisualizacao = modoSalvo
    }
  },

  computed: {
    ...mapState(["backendUrl"])
  },

  watch: {
    // Salvar preferência de visualização
    modoVisualizacao(novoModo) {
      localStorage.setItem('categorias_modo_visualizacao', novoModo)
    }
  },

  methods: {
    async carregarCategorias() {
      this.carregando = true

      try {
        const response = await axios.post(`${this.backendUrl}normativa/categoria`, {
          acao: 'listar'
        })

        this.categorias = response.data || []

      } catch (error) {
        console.error('Erro ao carregar categorias:', error)

        const mensagemErro = error.response?.data?.erro
            || error.message
            || 'Erro ao carregar categorias'

        this.mostrarSnackbar(mensagemErro, 'error')
      } finally {
        this.carregando = false
      }
    },

    abrirFormulario(categoria = null) {
      this.categoriaEditando = categoria
      this.dialogFormulario = true
    },

    handleCategoriaSalva() {
      this.carregarCategorias()
    },

    handleFormularioFechado() {
      this.categoriaEditando = null
    },

    visualizarDetalhes(categoria) {
      this.categoriaSelecionada = categoria
      this.dialogDetalhes = true
    },

    confirmarInativacao(categoria) {
      this.categoriaSelecionada = categoria
      this.dialogConfirmacao = true
    },

    async inativarCategoria() {
      if (!this.categoriaSelecionada) return

      this.processando = true

      try {
        await axios.post(`${this.backendUrl}normativa/categoria`, {
          acao: 'deletar',
          id: this.categoriaSelecionada.id
        })

        this.mostrarSnackbar('Categoria inativada com sucesso!', 'success')
        this.dialogConfirmacao = false
        this.categoriaSelecionada = null
        this.carregarCategorias()

      } catch (error) {
        console.error('Erro ao inativar categoria:', error)

        const mensagemErro = error.response?.data?.erro
            || error.message
            || 'Erro ao inativar categoria'

        this.mostrarSnackbar(mensagemErro, 'error')
      } finally {
        this.processando = false
      }
    },

    formatarData(dataString) {
      if (!dataString) return '-'

      try {
        const partes = dataString.split('-')

        if (partes.length >= 3) {
          const ano = partes[0]
          const mes = partes[1]
          const dia = partes[2]

          return `${dia}/${mes}/${ano}`
        }

        return '-'
      } catch (error) {
        console.error('Erro ao formatar data:', error, dataString)
        return '-'
      }
    },

    formatarDataCompleta(dataString) {
      if (!dataString) return '-'

      try {
        const partes = dataString.split('-')

        if (partes.length >= 4) {
          const ano = partes[0]
          const mes = partes[1]
          const dia = partes[2]

          const horaCompleta = partes[3]
          const partesHora = horaCompleta.split('.')

          const hora = partesHora[0] || '00'
          const minuto = partesHora[1] || '00'

          return `${dia}/${mes}/${ano} ${hora}:${minuto}`
        }

        return '-'
      } catch (error) {
        console.error('Erro ao formatar data completa:', error, dataString)
        return '-'
      }
    },

    mostrarSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
.categoria-lista-dashboard {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header Card */
.header-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
}

.header-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
}

/* View Toggle */
::v-deep .v-btn-toggle {
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .v-btn-toggle .v-btn {
  border-radius: 0 !important;
  border-color: #e2e8f0 !important;
}

::v-deep .v-btn-toggle .v-btn.v-btn--active {
  background-color: #023449 !important;
  color: white !important;
}

/* Action Button */
.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 8px;
  padding: 0 16px !important;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.loading-text {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

/* No Data Message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.no-data-message h3 {
  margin: 0;
  color: #023449;
  font-size: 1.25rem;
  font-weight: 700;
}

.no-data-message p {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
}

/* ========== VISUALIZAÇÃO EM CARDS ========== */

/* Categoria Card */
.categoria-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.categoria-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(2, 52, 73, 0.12);
}

.categoria-inativa {
  opacity: 0.65;
}

.categoria-inativa .categoria-header {
  filter: grayscale(50%);
}

/* Card Menu Button */
.card-menu-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.card-menu-btn:hover {
  background: rgba(0, 0, 0, 0.15);
}

/* Categoria Header */
.categoria-header {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  position: relative;
}

.categoria-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent);
}

/* Categoria Content */
.categoria-nome {
  font-size: 1.125rem;
  font-weight: 700;
  color: #023449;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

.categoria-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-text {
  font-size: 0.813rem;
  color: #64748b;
}

/* ========== VISUALIZAÇÃO EM LISTA ========== */

.lista-view {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.categoria-table {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.lista-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.lista-nome {
  font-size: 0.938rem;
  font-weight: 600;
  color: #023449;
  line-height: 1.3;
}

.lista-id {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 2px;
}

.lista-date {
  font-size: 0.813rem;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

/* Table Customization */
::v-deep .v-data-table thead th {
  background-color: #fafafa !important;
  color: #023449 !important;
  font-weight: 700 !important;
  font-size: 0.813rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

::v-deep .v-data-table tbody tr:hover {
  background-color: rgba(2, 52, 73, 0.02) !important;
}

::v-deep .v-data-table tbody td {
  padding: 12px 16px !important;
}

::v-deep .v-data-footer {
  border-top: 1px solid #e2e8f0;
  padding: 12px 16px;
}

/* Menu Items */
.menu-item-text {
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep .v-list-item {
  min-height: 40px;
}

::v-deep .v-list-item__icon {
  margin-right: 12px !important;
}

/* Dialog */
.dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
}

.detail-label {
  color: #023449;
  font-size: 0.875rem;
  padding: 12px 16px;
}

.detail-value {
  color: #1e293b;
  font-size: 0.875rem;
  padding: 12px 16px;
}

/* Responsive */
@media (max-width: 768px) {
  .header-card {
    border-radius: 8px;
  }

  .header-title {
    font-size: 1rem;
  }

  .categoria-card {
    border-radius: 8px;
  }

  .categoria-nome {
    font-size: 1rem;
  }

  .categoria-header {
    min-height: 80px;
    padding: 16px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .categoria-card {
    transition: none !important;
  }
}
</style>